/**
 * Super-admin platform-operator API — Batch 17 (2026-05-20).
 *
 * Typed hooks for the /superadmin/* surface. All endpoints are
 * super_admin-only + step-up; the layout guard at routes/superadmin/_layout.tsx
 * gates the surface, and APIM enforces the role claim independently.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';
import { useImpersonationControls } from '../acting/context';

/** Generate a fresh idempotency key per money-mutation attempt (mirrors
 *  billing.ts `newIdempotencyKey`). The backend `withVerifiedIdempotency`
 *  wrapper on the refund + billing-override endpoints ONLY dedupes when the
 *  caller sends an `idempotency-key` header — with no header it runs the handler
 *  every time, so a network retry of a refund/override could apply real money
 *  movement twice. Falls back to timestamp+random where crypto.randomUUID is
 *  unavailable (older embedded webviews). */
function newIdempotencyKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `superadmin-money-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ── Tenant rows + detail ────────────────────────────────────────────────────

export type SuperadminTenantLifecycle = 'pending' | 'active' | 'suspended' | 'deleted';

export interface SuperadminTenantRow {
  id: string;
  displayName: string;
  slug: string;
  lifecycleState: SuperadminTenantLifecycle;
  tier: string;
  userCount: number;
  trustCount: number;
  totalAumCents: number | null;
  mrrCents: number | null;
  lastActivityAt: string;
  hasBillingOverride: boolean;
}

export interface SuperadminTenantsListResponse {
  tenants: SuperadminTenantRow[];
  total: number;
  correlationId: string;
}

export interface SuperadminTenantDetailResponse {
  tenant: {
    id: string;
    displayName: string;
    slug: string;
    kind: 'b2b' | 'b2c';
    tier: string;
    jurisdiction: string;
    lifecycleState: SuperadminTenantLifecycle;
    createdAt: string;
    updatedAt: string;
    suspendedAt?: string;
    suspendedReason?: string;
    suspendedByUserId?: string;
    deletedAt?: string;
    onboardingCompletedAt?: string;
    billingOverride?: {
      tier: string;
      monthlyRand: number;
      expiresAt: string;
      reason: string;
      appliedByUserId: string;
      appliedAt: string;
    };
  };
  userCount: number;
  trustCount: number;
  deleteRequest: {
    status: 'collecting-quorum' | 'approved' | 'executed' | 'rejected';
    quorumCount: number;
    quorumRequired: number;
    approvers: { userId: string; ts: string }[];
  } | null;
  correlationId: string;
}

// ── List ────────────────────────────────────────────────────────────────────

function failFastOnSuperadminMissing(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && (error.status === 404 || error.status === 403)) {
    return false;
  }
  return failureCount < 3;
}

export function useSuperadminTenants(opts: { enabled?: boolean } = {}) {
  return useQuery<SuperadminTenantsListResponse>({
    queryKey: ['superadmin', 'tenants', 'list'],
    queryFn: ({ signal }) =>
      api<SuperadminTenantsListResponse>('/api/v1/superadmin/tenants', { signal }),
    enabled: opts.enabled ?? true,
    retry: failFastOnSuperadminMissing,
  });
}

export function useSuperadminTenant(id: string | undefined, opts: { enabled?: boolean } = {}) {
  return useQuery<SuperadminTenantDetailResponse>({
    queryKey: ['superadmin', 'tenants', 'detail', id ?? null],
    queryFn: ({ signal }) =>
      api<SuperadminTenantDetailResponse>(
        `/api/v1/superadmin/tenants/${encodeURIComponent(id ?? '')}`,
        { signal },
      ),
    enabled: !!id && (opts.enabled ?? true),
    retry: failFastOnSuperadminMissing,
  });
}

/**
 * Selects a SINGLE tenant row from the cross-tenant list query — SA-04.
 *
 * The tenant-detail handler (GET /tenants/{id}) does not project AUM/MRR, so
 * the detail page would render strictly LESS than the list it drilled from. The
 * list aggregator (GET /tenants) IS the source of `totalAumCents` + `mrrCents`,
 * and the super-admin layout already mounts `useSuperadminTenants()` on every
 * /superadmin/* view — so this row is normally already in cache. We `enabled`
 * the underlying query so a deep-link straight to the detail page (cold cache)
 * still resolves the figures rather than silently showing nothing.
 *
 * Returns the matched row (or undefined while loading / when the tenant isn't in
 * the list, e.g. a deleted tenant the list omits). The caller renders an honest
 * pending/empty state from `null`/`undefined` — never a fabricated number.
 */
export function useSuperadminTenantRow(id: string | undefined) {
  const listQ = useSuperadminTenants({ enabled: !!id });
  const row = id ? listQ.data?.tenants.find((t) => t.id === id) : undefined;
  return { row, isLoading: listQ.isLoading, isError: listQ.isError } as const;
}

// ── Per-tenant user directory (SA-07) ─────────────────────────────────────────
//
// GET /v1/superadmin/tenants/{id}/users — feeds the reset-password + impersonate
// PICKERS so the operator selects a user from a searchable list instead of
// pasting a raw UUID. The projection is PII-MINIMAL by design (userId,
// displayName, role, status, createdAt) — NO email / phone. The raw-UUID input
// remains as a clearly-labelled advanced fallback for the rare case a user isn't
// in the list (e.g. a tenant larger than the server's MAX_USERS cap).

export type SuperadminTenantUserStatus =
  | 'active' | 'invited' | 'deceased' | 'incapacitated' | 'suspended';

export interface SuperadminTenantUserRow {
  userId: string;
  displayName: string;
  role: string;
  status: SuperadminTenantUserStatus;
  createdAt: string;
}

export interface SuperadminTenantUsersListResponse {
  tenantId: string;
  users: SuperadminTenantUserRow[];
  total: number;
  correlationId: string;
}

export function useSuperadminTenantUsers(
  id: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<SuperadminTenantUsersListResponse>({
    queryKey: ['superadmin', 'tenants', 'users', id ?? null],
    queryFn: ({ signal }) =>
      api<SuperadminTenantUsersListResponse>(
        `/api/v1/superadmin/tenants/${encodeURIComponent(id ?? '')}/users`,
        { signal },
      ),
    enabled: !!id && (opts.enabled ?? true),
    retry: failFastOnSuperadminMissing,
  });
}

// ── Suspend / reactivate / delete ───────────────────────────────────────────

export interface SuspendTenantInput { id: string; reason: string }
export interface SuspendTenantResponse {
  tenant: { id: string; displayName: string; lifecycleState: SuperadminTenantLifecycle; suspendedAt?: string; suspendedReason?: string };
  correlationId: string;
}

export function useSuspendTenant() {
  const qc = useQueryClient();
  return useMutation<SuspendTenantResponse, ApiError, SuspendTenantInput>({
    mutationFn: ({ id, reason }) =>
      api<SuspendTenantResponse>(
        `/api/v1/superadmin/tenants/${encodeURIComponent(id)}/suspend`,
        { method: 'POST', body: { reason } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['superadmin', 'tenants'] });
    },
  });
}

export interface ReactivateTenantInput { id: string }
export interface ReactivateTenantResponse {
  tenant: { id: string; displayName: string; lifecycleState: SuperadminTenantLifecycle };
  correlationId: string;
}

export function useReactivateTenant() {
  const qc = useQueryClient();
  return useMutation<ReactivateTenantResponse, ApiError, ReactivateTenantInput>({
    mutationFn: ({ id }) =>
      api<ReactivateTenantResponse>(
        `/api/v1/superadmin/tenants/${encodeURIComponent(id)}/reactivate`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['superadmin', 'tenants'] });
    },
  });
}

export interface RequestDeleteTenantInput { id: string; reason: string }
export interface RequestDeleteTenantResponse {
  status: 'collecting-quorum' | 'approved' | 'executed' | 'rejected';
  quorumCount: number;
  quorumRequired: number;
  tenantDeleted: boolean;
  correlationId: string;
}

export function useRequestDeleteTenant() {
  const qc = useQueryClient();
  return useMutation<RequestDeleteTenantResponse, ApiError, RequestDeleteTenantInput>({
    mutationFn: ({ id, reason }) =>
      api<RequestDeleteTenantResponse>(
        `/api/v1/superadmin/tenants/${encodeURIComponent(id)}/delete-request`,
        { method: 'POST', body: { reason, confirmIrreversible: true } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['superadmin', 'tenants'] });
    },
  });
}

// ── Update tenant details (displayName / slug) ──────────────────────────────
//
// PATCH /v1/tenants/{id} (backend/src/handlers/tenants-patch.ts, Phase 11) —
// NOT under /superadmin/*: it's the tenant-admin-write endpoint any admin/
// super_admin can call on their own tenant, and a super_admin can call on
// ANY tenant. Step-up REQUIRED server-side (slug affects the AES handoff
// host) — handled for free by the global StepUpInterceptor (step-up-
// interceptor.tsx), which queues any STEP_UP_REQUIRED mutation failure,
// runs the MFA challenge in place, and replays it. No per-call step-up
// wiring needed here.
//
// Previously built with no caller anywhere in the frontend (QA finding
// wire-judgment, 2026-07-16 re-trace) — the tenant-detail screen rendered
// displayName/slug read-only with zero edit path.

export interface UpdateTenantInput {
  id: string;
  displayName?: string;
  slug?: string;
}

export interface UpdateTenantResponse {
  tenant: {
    id: string;
    kind: 'b2b' | 'b2c';
    displayName: string;
    slug: string;
    tier: string;
    jurisdiction: string;
    createdAt: string;
    updatedAt: string;
  };
  correlationId: string;
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation<UpdateTenantResponse, ApiError, UpdateTenantInput>({
    mutationFn: ({ id, ...patch }) =>
      api<UpdateTenantResponse>(
        `/api/v1/tenants/${encodeURIComponent(id)}`,
        { method: 'PATCH', body: patch },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['superadmin', 'tenants'] });
    },
  });
}

// ── Impersonation ───────────────────────────────────────────────────────────

export interface ImpersonateStartInput { targetUserId: string; reason: string }
export interface ImpersonateStartResponse {
  impersonationJwt: string;
  targetUserId: string;
  targetUserDisplay: string;
  targetTenantId: string;
  expiresAt: string;
  correlationId: string;
}

export function useStartImpersonation() {
  const { beginImpersonation: setImpersonation } = useImpersonationControls();
  return useMutation<ImpersonateStartResponse, ApiError, ImpersonateStartInput>({
    mutationFn: (body) =>
      api<ImpersonateStartResponse>('/api/v1/superadmin/impersonate/start', { method: 'POST', body }),
    onSuccess: (res) => {
      // Activate impersonation so api() switches to the minted JWT and the
      // banner mounts on /app/*.
      setImpersonation({
        jwt: res.impersonationJwt,
        targetUserId: res.targetUserId,
        targetDisplay: res.targetUserDisplay,
        expiresAt: res.expiresAt,
      });
    },
  });
}

export function useStopImpersonation() {
  return useMutation<{ stoppedAt?: string; targetUserId: string; correlationId: string }, ApiError, void>({
    // useOperatorSession: the stop endpoint is super_admin + step-up gated, so
    // it must travel on the operator's own session, not the impersonation JWT.
    // The store-clear + navigation are owned by the caller (the impersonation
    // banner) so they run before the banner unmounts — see its handleLeave.
    mutationFn: () =>
      api('/api/v1/superadmin/impersonate/stop', { method: 'POST', body: {}, useOperatorSession: true }),
  });
}

// ── Tenant create + billing override + force password reset ─────────────────

export interface CreateTenantInput {
  displayName: string;
  slug: string;
  ownerEmail: string;
  tier: 'starter' | 'professional' | 'enterprise';
}
export interface CreateTenantResponse {
  tenant: { id: string; displayName: string; slug: string; tier: string; lifecycleState: SuperadminTenantLifecycle };
  nextStep: { action: string; endpoint: string; body: Record<string, unknown> };
  correlationId: string;
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation<CreateTenantResponse, ApiError, CreateTenantInput>({
    mutationFn: (body) =>
      api<CreateTenantResponse>('/api/v1/superadmin/tenants', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['superadmin', 'tenants'] });
    },
  });
}

export interface ApplyBillingOverrideInput {
  id: string;
  tier: string;
  monthlyRand: number;
  expiresAt: string;
  reason: string;
}
export interface ApplyBillingOverrideResponse {
  tenant: { id: string; billingOverride: SuperadminTenantDetailResponse['tenant']['billingOverride'] };
  correlationId: string;
}

export function useApplyBillingOverride() {
  const qc = useQueryClient();
  return useMutation<ApplyBillingOverrideResponse, ApiError, ApplyBillingOverrideInput>({
    mutationFn: ({ id, ...body }) =>
      api<ApplyBillingOverrideResponse>(
        `/api/v1/superadmin/tenants/${encodeURIComponent(id)}/billing-override`,
        {
          method: 'POST',
          body,
          // Money-mutation safety: the backend wraps this in
          // withVerifiedIdempotency; a fresh key per attempt lets a
          // double-submit replay instead of re-pinning the override twice.
          headers: { 'idempotency-key': newIdempotencyKey() },
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['superadmin', 'tenants'] });
    },
  });
}

export interface ForcePasswordResetInput { userId: string; reason: string }
export interface ForcePasswordResetResponse {
  linkDispatched?: boolean;
  noticeFlags?: { supabaseAdminSkipped?: boolean; supabaseUrlMissing?: boolean };
  message?: string;
  correlationId: string;
}

export function useForcePasswordReset() {
  return useMutation<ForcePasswordResetResponse, ApiError, ForcePasswordResetInput>({
    mutationFn: ({ userId, reason }) =>
      api<ForcePasswordResetResponse>(
        `/api/v1/superadmin/users/${encodeURIComponent(userId)}/force-password-reset`,
        { method: 'POST', body: { reason } },
      ),
  });
}

// ── Billing refund / credit ──────────────────────────────────────────────────
//
// POST /v1/superadmin/tenants/{id}/billing/refund (REQ-BILL-003).
// Paystack has no separate "credit balance" primitive — a refund against the
// most-recent paid transaction IS the credit mechanism. The operator supplies
// the Paystack transaction `reference` (visible on the invoice list), a
// mandatory reason, and an OPTIONAL partial amount in Rands (omit `amountRand`
// for a FULL refund of the original transaction). Body shape mirrors the
// handler's BillingRefundSchema exactly.

export interface SuperadminBillingRefundInput {
  id: string;
  /** Paystack transaction reference to refund (NOT the subscription code). */
  transactionReference: string;
  /** Optional partial-refund amount in Rands. Omit for a FULL refund. */
  amountRand?: number;
  reason: string;
}
export interface SuperadminBillingRefundResponse {
  refund: {
    tenantId: string;
    transactionReference: string;
    amountKobo: number | null;
    full: boolean;
    status: string;
    paystackRefundId: string;
  };
  correlationId: string;
}

export function useSuperadminBillingRefund() {
  const qc = useQueryClient();
  return useMutation<SuperadminBillingRefundResponse, ApiError, SuperadminBillingRefundInput>({
    mutationFn: ({ id, ...body }) =>
      api<SuperadminBillingRefundResponse>(
        `/api/v1/superadmin/tenants/${encodeURIComponent(id)}/billing/refund`,
        {
          method: 'POST',
          body,
          // Money-mutation safety: the backend wraps this in
          // withVerifiedIdempotency; a fresh key per attempt lets a
          // double-submit / retry replay instead of issuing two refunds.
          headers: { 'idempotency-key': newIdempotencyKey() },
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['superadmin', 'tenants'] });
    },
  });
}
